/**
 * Web Push helper — subscribe/unsubscribe the current browser to Prenotoo push.
 * Uses axios `api` instance (baseURL = REACT_APP_BACKEND_URL/api).
 *
 * IMPORTANT: the "active" state of push notifications requires THREE conditions:
 *   1. Notification.permission === "granted"
 *   2. Browser pushManager has a subscription
 *   3. Backend has the subscription stored under the CURRENT user_id
 *
 * Without (3) the backend cannot deliver push to this device.
 * `getFullStatus()` returns all three flags so the UI can react accordingly.
 */
import { api } from "./api";

const log = (...args) => console.info("[push]", ...args);
const warn = (...args) => console.warn("[push]", ...args);

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Wait for the SW to be ready (registered & active). Throws if not registered. */
async function ready() {
  if (!pushSupported()) throw new Error("Notifiche non supportate su questo browser");
  // navigator.serviceWorker.ready resolves only when there's a registered+active SW
  const reg = await navigator.serviceWorker.ready;
  log("service-worker ready", { scope: reg.scope, active: !!reg.active });
  return reg;
}

export async function getSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/**
 * Returns the full push registration status (browser + backend).
 * { supported, permission, browserSub, backendCount, backendMatch, active, needsResync }
 */
export async function getFullStatus() {
  const status = {
    supported: pushSupported(),
    permission: typeof Notification !== "undefined" ? Notification.permission : "default",
    browserSub: null,        // raw PushSubscription from pushManager (or null)
    backendCount: 0,         // total subscriptions for current user on backend
    backendMatch: false,     // backend has THIS browser's endpoint for current user
    active: false,           // truthy = device is fully ready to receive
    needsResync: false,      // permission granted + browser sub but backend doesn't know
    serverEnabled: false,    // VAPID configured server-side
    error: null,
  };
  if (!status.supported) return status;
  try {
    const reg = await ready();
    status.browserSub = await reg.pushManager.getSubscription();
  } catch (e) {
    status.error = e.message || String(e);
    warn("status: SW not ready", e);
    return status;
  }
  let endpoint = null;
  if (status.browserSub) endpoint = status.browserSub.endpoint;
  try {
    const { data } = await api.get("/push/status", { params: endpoint ? { endpoint } : {} });
    status.serverEnabled = !!data.enabled;
    status.backendCount = data.count || 0;
    status.backendMatch = !!data.this_device_registered;
  } catch (e) {
    // Not fatal: e.g. user not authenticated → backend returns 401, treat as unknown
    status.error = e.response?.status === 401 ? "Non autenticato" : (e.message || "Errore stato push");
  }
  status.active = !!(
    status.permission === "granted" &&
    status.browserSub &&
    status.backendMatch
  );
  status.needsResync = !!(
    status.permission === "granted" &&
    status.browserSub &&
    !status.backendMatch &&
    status.serverEnabled
  );
  log("full-status", {
    supported: status.supported,
    permission: status.permission,
    hasBrowserSub: !!status.browserSub,
    backendCount: status.backendCount,
    backendMatch: status.backendMatch,
    active: status.active,
    needsResync: status.needsResync,
  });
  return status;
}

/**
 * Subscribe browser AND register with backend. Idempotent — safe to call when
 * a subscription already exists locally (will re-POST to backend to ensure it's
 * stored under the CURRENT user_id).
 */
export async function subscribePush({ resyncOnly = false } = {}) {
  if (!pushSupported()) throw new Error("Notifiche non supportate su questo browser");

  let perm = Notification.permission;
  if (perm !== "granted") {
    if (resyncOnly) throw new Error("Permesso notifiche non concesso");
    perm = await Notification.requestPermission();
  }
  log("permission", perm);
  if (perm !== "granted") throw new Error("Permesso notifiche negato");

  const { data: key } = await api.get("/push/vapid-key");
  if (!key.enabled || !key.public_key) throw new Error("Server non configurato per le notifiche");
  log("vapid-key-loaded", { enabled: key.enabled });

  const reg = await ready();
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    log("existing browser subscription", { endpoint: sub.endpoint.slice(0, 60) + "…" });
  } else {
    log("creating new browser subscription via pushManager.subscribe()");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key.public_key),
    });
    log("new subscription created", { endpoint: sub.endpoint.slice(0, 60) + "…" });
  }

  const payload = sub.toJSON();
  const { data } = await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
  });
  log("subscription saved on backend", data);
  return sub;
}

/** Unsubscribe browser + remove from backend. */
export async function unsubscribePush() {
  const sub = await getSubscription();
  if (!sub) {
    log("nothing to unsubscribe (no browser subscription)");
    return false;
  }
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (e) {
    warn("backend unsubscribe failed (continuing locally)", e);
  }
  try {
    await sub.unsubscribe();
  } catch (e) {
    warn("local unsubscribe failed", e);
  }
  return true;
}

export async function testPush() {
  const { data } = await api.post("/push/test");
  log("test response", data);
  return data;
}
